(() => {
  'use strict';

  const root = document.querySelector('[data-pw8-consent-root]');
  if (!root) return;

  let config = {};
  try { config = JSON.parse(root.querySelector('[data-pw8-consent-config]')?.textContent || '{}'); } catch {}
  if (config.enabled === false) { root.hidden = true; return; }

  const panel = root.querySelector('[data-pw8-consent-panel]');
  const panelDialog = panel?.querySelector('[role="dialog"]');
  const analytics = root.querySelector('[data-pw8-consent-analytics]');
  const marketing = root.querySelector('[data-pw8-consent-marketing]');
  const preferences = root.querySelector('[data-pw8-consent-preferences]');
  const status = root.querySelector('[data-pw8-consent-status]');
  const storageKey = 'pielurd-consent-v2';
  let api = null;
  let lastFocus = null;

  const focusables = container => [...(container?.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])') || [])]
    .filter(element => !element.hidden && element.offsetParent !== null);

  const readLocal = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || 'null'); }
    catch { return null; }
  };
  const saveLocal = choices => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        analytics: Boolean(choices.analytics),
        marketing: Boolean(choices.marketing),
        preferences: Boolean(choices.preferences),
        saleOfData: false,
        version: 2,
        updatedAt: new Date().toISOString()
      }));
    } catch {}
  };
  const currentFromApi = () => {
    try { return api?.currentVisitorConsent?.() || null; }
    catch { return null; }
  };
  const hasCoreDecision = visitor => ['analytics', 'marketing', 'preferences'].every(key => visitor?.[key] === 'yes' || visitor?.[key] === 'no');
  const normalize = visitor => ({
    analytics: visitor?.analytics === 'yes',
    marketing: visitor?.marketing === 'yes',
    preferences: visitor?.preferences === 'yes'
  });
  const payload = choices => ({
    analytics: Boolean(choices.analytics),
    marketing: Boolean(choices.marketing),
    preferences: Boolean(choices.preferences),
    sale_of_data: false
  });
  const allowed = fallback => ({
    analytics: api?.analyticsProcessingAllowed?.() ?? Boolean(fallback?.analytics),
    marketing: api?.marketingAllowed?.() ?? Boolean(fallback?.marketing),
    preferences: api?.preferencesProcessingAllowed?.() ?? Boolean(fallback?.preferences)
  });
  const emit = (choices, source) => {
    window.PielurdConsent = { ...choices, source, ready: true };
    document.dispatchEvent(new CustomEvent('pielurd:consent', { detail: window.PielurdConsent }));
  };
  const syncInputs = choices => {
    if (analytics) analytics.checked = Boolean(choices.analytics);
    if (marketing) marketing.checked = Boolean(choices.marketing);
    if (preferences) preferences.checked = Boolean(choices.preferences);
  };
  const show = () => { root.hidden = false; };
  const closePanel = (restoreFocus = true) => {
    if (panel) {
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('pw8-consent-panel-open');
    if (readLocal()) root.hidden = true;
    if (restoreFocus) lastFocus?.focus?.({ preventScroll: true });
  };
  const hide = () => {
    root.hidden = true;
    if (panel) {
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('pw8-consent-panel-open');
  };
  const openPanel = () => {
    lastFocus = document.activeElement;
    if (panel) {
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
    }
    root.hidden = false;
    document.body.classList.add('pw8-consent-panel-open');
    requestAnimationFrame(() => focusables(panelDialog || panel)[0]?.focus({ preventScroll: true }));
  };
  const updateVisibility = () => {
    const visitor = currentFromApi();
    const saved = readLocal();
    const undecided = visitor ? (!hasCoreDecision(visitor) && !saved) : !saved;
    let shouldShow = Boolean(config.showEverywhere && undecided);
    try { shouldShow = shouldShow || Boolean(!saved && api?.shouldShowBanner?.()); } catch {}
    root.hidden = !shouldShow;
  };
  const setShopifyConsent = (choices, callback) => {
    if (!api?.setTrackingConsent) { callback?.(null); return; }
    try { api.setTrackingConsent(payload(choices), error => callback?.(error || null)); }
    catch (error) { callback?.(error); }
  };
  const capture = choices => {
    const normalized = {
      analytics: Boolean(choices.analytics),
      marketing: Boolean(choices.marketing),
      preferences: Boolean(choices.preferences)
    };
    syncInputs(normalized);
    saveLocal(normalized);
    setShopifyConsent(normalized, error => {
      const effective = error ? normalized : allowed(normalized);
      emit(effective, error ? 'local-choice' : 'choice');
      if (status) status.textContent = error ? 'Tercihiniz bu cihazda kaydedildi.' : 'Tercihleriniz kaydedildi.';
      hide();
      lastFocus?.focus?.({ preventScroll: true });
    });
  };

  root.addEventListener('click', event => {
    if (event.target.closest('[data-pw8-consent-accept]')) capture({ analytics: true, marketing: true, preferences: true });
    else if (event.target.closest('[data-pw8-consent-reject]')) capture({ analytics: false, marketing: false, preferences: false });
    else if (event.target.closest('[data-pw8-consent-open]')) openPanel();
    else if (event.target.closest('[data-pw8-consent-close]')) closePanel(true);
    else if (event.target.closest('[data-pw8-consent-save]')) capture({ analytics: Boolean(analytics?.checked), marketing: Boolean(marketing?.checked), preferences: Boolean(preferences?.checked) });
    else if (event.target === panel) closePanel(true);
  });

  document.addEventListener('click', event => {
    if (!event.target.closest('[data-pw8-open-consent]')) return;
    event.preventDefault();
    const visitor = currentFromApi();
    const saved = readLocal();
    const choices = visitor && hasCoreDecision(visitor) ? normalize(visitor) : (saved || normalize(visitor));
    syncInputs(choices);
    show();
    openPanel();
  });

  document.addEventListener('keydown', event => {
    if (!panel || panel.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel(true);
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables(panelDialog || panel);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  const init = () => {
    api = window.Shopify?.customerPrivacy || null;
    const visitor = currentFromApi();
    const saved = readLocal();
    const choices = visitor && hasCoreDecision(visitor) ? normalize(visitor) : (saved || normalize(visitor));
    syncInputs(choices);
    emit(allowed(choices), 'initial');
    const requiresApiSync = Boolean(saved && api?.setTrackingConsent && (!hasCoreDecision(visitor) || visitor?.sale_of_data === ''));
    if (requiresApiSync) {
      setShopifyConsent(saved, () => { emit(allowed(saved), 'sync'); updateVisibility(); });
    } else updateVisibility();
  };

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => init());
  } else init();
})();
